/**
 * Dispatcher de push por plataforma.
 *
 * Expone la MISMA superficie que `src/services/push/pushClient.ts` y decide en
 * runtime:
 *   - Web      → delega en el cliente Web Push existente (intacto).
 *   - Nativo   → delega en `nativePush.ts` (FCM vía Capacitor), cargado con
 *                import dinámico para que no entre en el bundle web.
 *
 * Los consumidores (PushNotificationsContext, AuthContext, StudentAreaWrapper)
 * importan de aquí en vez de importar pushClient directamente.
 */

import {
  disablePush as webDisablePush,
  ensurePushForCurrentUser as webEnsurePush,
  hasActiveSubscription as webHasActiveSubscription,
  pushSupported as webPushSupported,
  requestAndSubscribe as webRequestAndSubscribe,
} from '@/services/push/pushClient';
import { isNative } from '../platform';

export type PushPermission = 'default' | 'granted' | 'denied' | 'unsupported';

/** En nativo el push siempre está disponible (FCM/APNs). */
export const pushSupported = isNative() ? true : webPushSupported;

/**
 * Lectura síncrona del permiso de notificaciones, multiplataforma.
 * En web equivale al viejo `Notification.permission`; en nativo lee el cache
 * del plugin (refrescado en cada operación async).
 */
export function readPushPermission(): PushPermission {
  if (isNative()) {
    // El módulo nativo ya está cargado si alguna operación async corrió antes;
    // para la primera lectura síncrona devolvemos el estado por defecto.
    return nativeModule ? nativeModule.readNativePushPermission() : 'default';
  }
  return pushSupported ? (Notification.permission as PushPermission) : 'unsupported';
}

/** Módulo nativo cacheado (solo se importa una vez, y solo en nativo). */
let nativeModule: typeof import('./nativePush') | null = null;

async function native(): Promise<typeof import('./nativePush')> {
  if (!nativeModule) {
    nativeModule = await import('./nativePush');
  }
  return nativeModule;
}

export async function ensurePushForCurrentUser(): Promise<void> {
  if (isNative()) {
    return (await native()).ensureNativePushForCurrentUser();
  }
  return webEnsurePush();
}

export async function requestAndSubscribe(): Promise<boolean> {
  if (isNative()) {
    return (await native()).requestAndSubscribeNative();
  }
  return webRequestAndSubscribe();
}

export async function disablePush(): Promise<void> {
  if (isNative()) {
    return (await native()).disableNativePush();
  }
  return webDisablePush();
}

export async function hasActiveSubscription(): Promise<boolean> {
  if (isNative()) {
    return (await native()).hasActiveNativeSubscription();
  }
  return webHasActiveSubscription();
}
